import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Tabs } from "@mantine/core";
import { Spinner } from "../components/Spinner";
import { loadedAt } from "../lib/format";
import { recallListPlace, rememberListPlace } from "../lib/listMemory";
import { Button } from "./Button";
import { IconArrowLeft } from "./icons";
import { STRINGS } from "./strings";

/* The shape every record page has.
 *
 * Designed in the Companies design pass (docs/ux-decisions.md, A record is a
 * page) and shared once People and Properties needed the same: a header band
 * with the identity, the verdict, the actions and the account's figures, and
 * then tabs at full width, Details first (src/ui/Facts.tsx) and the related
 * records after it, each a table. The tab is in the URL as ?tab=, so a link
 * to a record's properties opens on them.
 *
 * The way back, after the 2026-10-08 audit. A record is reached from a list,
 * from another record, and from a link pasted into Orum or a note, and the
 * back link says where it goes: "Back to people" from the list, "Back to
 * Acme Holdings" from the company the person was found on, "Back" when the
 * page before was something else in this app, and the list when there was
 * no page before. The list, or the record, the person came from remembers
 * its scroll position and the row they opened (src/lib/listMemory.ts), and
 * restores both.
 *
 * There was a band of facts between the header and the tabs until
 * 2026-10-08. It went when James put Details first: the same facts on the
 * landing tab and in a band above it were one record shown twice.
 *
 * A page owns its queries and its tabs' contents. This owns the frame. */
export type RecordFigure = {
  n: number | null | undefined;
  label: string;
  /** The tab the figure opens. Without one the figure is a figure, not a button. */
  tab?: string;
  pending?: boolean;
};

export type RecordAction = {
  label: string;
  /** The tab and the section id the action opens, for an action that is a
   *  form further down the page. Absent for an action that opens a dialog or
   *  another page. */
  tab?: string;
  anchor?: string;
  /** What the action does besides opening the tab, such as opening a form. */
  onSelect?: () => void;
};

export type RecordTab = {
  key: string;
  label: string;
  count?: number | null | undefined;
  content: ReactNode;
};

/** What a record page tells the links and tables inside it: where they are,
 *  so a hop to another record can say where it came from and the way back
 *  lands on the same row. */
export type RecordNav = {
  /** The page's own key, such as "companies". */
  key: string;
  /** The record's title, for "Back to <title>" on the page a link opens. */
  title: string;
  /** Remember the scroll position and the opened row before leaving. */
  remember: (rowId?: string | null) => void;
  /** Remember, then open another record's page. */
  open: (path: string, rowId?: string | null) => void;
  /** The row opened last time, to mark on the way back. */
  lastOpened: string | null;
};

const RecordNavContext = createContext<RecordNav | null>(null);
export const useRecordNav = () => useContext(RecordNavContext);

export function RecordPage({
  backLabel, backTo, fromKey, banner, loading, error, notFound,
  title, aka = [], facts, badges, status, beforeTabs, actions = [], figures = [], tabs, defaultTab,
  loadedAt: loadedMs, refreshing, onRefresh,
}: {
  backLabel: string;
  backTo: string;
  /** What the list writes into location.state.from when it opens a record. */
  fromKey: string;
  /** A line above the header that must not be missed, such as do not contact. */
  banner?: ReactNode;
  loading: boolean;
  error?: string | null;
  /** The sentence for a record that does not exist. */
  notFound?: string | null;
  title: string;
  /** Other names the record is known by. */
  aka?: readonly string[];
  /** The line under the name: website and place, title and employer. */
  facts?: ReactNode;
  badges?: ReactNode;
  /** Under the name and the facts, in the header's main column: the
   *  record's next step, such as a campaign's action and its line. */
  status?: ReactNode;
  /** A section of its own between the header and the tabs, such as a
   *  campaign's stage flow. Not part of the header, which says what the
   *  record is. */
  beforeTabs?: ReactNode;
  actions?: readonly RecordAction[];
  figures?: readonly RecordFigure[];
  tabs: readonly RecordTab[];
  defaultTab: string;
  /** When the record's own row loaded, for the loaded-at line. */
  loadedAt?: number;
  refreshing?: boolean;
  onRefresh?: () => void;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [search, setSearch] = useSearchParams();
  const asked = search.get("tab");
  const tab = tabs.some((t) => t.key === asked) ? (asked as string) : defaultTab;
  const [anchor, setAnchor] = useState<string | null>(null);

  const setTab = (next: string) => {
    const p = new URLSearchParams(search);
    if (next === defaultTab) p.delete("tab"); else p.set("tab", next);
    // Replaced, with the state kept: a tab is a view of this page, so Back
    // leaves the page rather than stepping through its tabs, and the back
    // link keeps saying where it goes. Found walking a campaign, 2026-10-08.
    setSearch(p, { replace: true, state: location.state });
  };

  /* The way back. location.key is "default" only for the first entry in
     this tab's history, which is a pasted link or a bookmark. */
  const came = (location.state ?? null) as { from?: string; back?: string } | null;
  const inApp = location.key !== "default";
  const backText = came?.back ? `${STRINGS.back} to ${came.back}`
    : came?.from === fromKey || !inApp ? backLabel
    : STRINGS.back;
  const back = () => { if (inApp) navigate(-1); else navigate(backTo); };

  /* This page's own place, for a hop to another record and back. */
  const placeKey = `rec:${location.pathname}${location.search}`;
  const [lastOpened, setLastOpened] = useState<string | null>(null);
  const restored = useRef(false);
  useEffect(() => {
    if (loading || restored.current) return;
    restored.current = true;
    const place = recallListPlace(placeKey);
    if (!place) return;
    setLastOpened(place.openId);
    window.scrollTo({ top: place.scrollY });
  }, [loading, placeKey]);
  const remember = (rowId?: string | null) => rememberListPlace(placeKey, { scrollY: window.scrollY, openId: rowId ?? null });
  const nav: RecordNav = {
    key: fromKey,
    title,
    remember,
    open: (path, rowId) => { remember(rowId); navigate(path, { state: { from: fromKey, back: title } }); },
    lastOpened,
  };

  const go = (a: RecordAction) => {
    if (a.tab) setTab(a.tab);
    if (a.anchor) setAnchor(a.anchor);
    a.onSelect?.();
  };
  useEffect(() => {
    if (!anchor) return;
    const el = document.getElementById(anchor);
    if (!el) return;
    el.scrollIntoView({ block: "start" });
    setAnchor(null);
  }, [tab, anchor]);

  const top = (
    <div className="rp-top">
      <Button kind="link" className="rp-back ui-back" onClick={back} leftSection={<IconArrowLeft />}>{backText}</Button>
      {onRefresh ? (
        <span className="rp-loaded muted">
          {loadedMs ? loadedAt(loadedMs) : null}
          <Button kind="link" size="compact-xs" onClick={onRefresh} disabled={refreshing || loading}>
            {refreshing ? "Refreshing" : "Refresh"}
          </Button>
        </span>
      ) : null}
    </div>
  );

  if (error) {
    return <main className="rp">{top}<p className="refusal" role="alert">{error}</p></main>;
  }
  if (notFound) {
    return <main className="rp">{top}<p className="refusal">{notFound}</p></main>;
  }

  const more = aka.length > 4 ? aka.length - 4 : 0;

  return (
    <RecordNavContext.Provider value={nav}>
      {/* The frame is drawn before the record arrives, so nothing moves when
          it does. The title and the figures wait, and the tabs are there. */}
      <main className="rp" aria-busy={loading}>
        {top}
        {banner}
        <header className="rp-head">
          <div className="rp-head-main">
            <h1>{loading ? <span className="muted"><Spinner />Loading</span> : title}</h1>
            {aka.length ? (
              <p className="rp-aka">
                also {aka.slice(0, 4).join(", ")}{more ? ` and ${more} more` : ""}
              </p>
            ) : null}
            {facts ? <p className="rp-facts">{facts}</p> : null}
            {badges ? <div className="rp-badges">{badges}</div> : null}
            </div>
          {/* The actions as links in view at the right of the header, never
              behind a menu button. A menu is for a table row, which has no
              room for more. A page has the room, and an action in a menu is
              one nobody finds. James, 2026-10-08. */}
          {actions.length ? (
            <nav className="rp-actions" aria-label="Actions">
              {actions.map((a) => <Button key={a.label} kind="link" onClick={() => go(a)} disabled={loading}>{a.label}</Button>)}
            </nav>
          ) : null}
          {status ? <div className="rp-status">{status}</div> : null}
          {figures.length ? (
            <div className="rp-figures">
              {figures.map((f) => {
                const n = f.n === null || f.n === undefined
                  ? (f.pending || loading ? <Spinner /> : <span className="muted" title="Not counted yet">-</span>)
                  : f.n.toLocaleString();
                const body = <><span className="rp-figure-n">{n}</span><span className="rp-figure-l">{f.label}</span></>;
                /* A figure with no tab behind it is a figure, not a button
                   that does nothing. */
                return f.tab
                  ? <button key={f.label} type="button" className="rp-figure" onClick={() => setTab(f.tab as string)}>{body}</button>
                  : <div key={f.label} className="rp-figure rp-figure-static">{body}</div>;
              })}
            </div>
          ) : null}
        </header>
        {beforeTabs}

        <Tabs value={tab} onChange={(v) => { if (v) setTab(v); }} className="rp-tabs" keepMounted={false}>
          <Tabs.List>
            {tabs.map((t) => (
              <Tabs.Tab key={t.key} value={t.key}>
                {t.label}
                {t.count === null || t.count === undefined ? null : <span className="rp-tab-count">{t.count.toLocaleString()}</span>}
              </Tabs.Tab>
            ))}
          </Tabs.List>
          {tabs.map((t) => (
            <Tabs.Panel key={t.key} value={t.key}>
              {t.content ?? <p className="muted rp-tabbody"><Spinner />{STRINGS.loading}</p>}
            </Tabs.Panel>
          ))}
        </Tabs>
      </main>
    </RecordNavContext.Provider>
  );
}
